import { useEffect, useMemo, useState } from "react";
import documentGroupApi from "services/document-group-api";
import { useGetMetadocsForFileQuery } from "services/metadoc-api";
import type { Metadoc } from "services/metadoc-api";
import { useDispatch } from "utils/redux";

export const useMetadocsWithAiResultsForFile = (fileId: number | undefined) => {
  const dispatch = useDispatch();
  const {
    data,
    isLoading: isLoadingMetadocs,
    isFetching,
  } = useGetMetadocsForFileQuery(
    { fileId: fileId?.toString() ?? "", limit: "100" },
    { skip: !fileId },
  );

  const metadocIdsKey = useMemo(
    () =>
      (data?.results ?? [])
        .map((metadoc) => `${metadoc.id}:${metadoc.name}`)
        .join(","),
    [data?.results],
  );

  const [metadocsWithAiResults, setMetadocsWithAiResults] = useState<Metadoc[]>(
    [],
  );
  const [isCheckingAiResults, setIsCheckingAiResults] = useState(false);

  useEffect(() => {
    const metadocs = data?.results ?? [];

    if (!fileId || metadocs.length === 0) {
      setMetadocsWithAiResults([]);
      setIsCheckingAiResults(false);
      return;
    }

    let cancelled = false;
    setIsCheckingAiResults(true);

    const checkMetadocs = async () => {
      const withAiResults: Metadoc[] = [];

      await Promise.all(
        metadocs.map(async (metadoc) => {
          try {
            const groups = await dispatch(
              documentGroupApi.endpoints.getDocumentGroups.initiate(
                metadoc.id,
                { forceRefetch: false },
              ),
            ).unwrap();

            if (groups.some((group) => group.source === "AI")) {
              withAiResults.push(metadoc);
            }
          } catch {
            // Ignore individual metadoc lookup failures.
          }
        }),
      );

      if (!cancelled) {
        withAiResults.sort(
          (left, right) =>
            new Date(right.created).getTime() -
            new Date(left.created).getTime(),
        );
        setMetadocsWithAiResults(withAiResults);
        setIsCheckingAiResults(false);
      }
    };

    void checkMetadocs();

    return () => {
      cancelled = true;
    };
  }, [dispatch, fileId, metadocIdsKey]);

  return {
    metadocsWithAiResults,
    hasMetadocsWithAiResults: metadocsWithAiResults.length > 0,
    isLoading: isLoadingMetadocs || isFetching || isCheckingAiResults,
  };
};
